import React, {useEffect, useMemo, useState} from 'react'
import {KTCard, KTCardBody} from '../../../../_websoft/helpers'
import {useParams, useNavigate} from 'react-router-dom'
import {Table, Form, InputGroup, Button} from 'react-bootstrap'
import {getNaloziZaTraku, obrisiStavkuSaPripremePaneli} from '../../../api/paneliPromet'
import {PripremaPrometaModel} from '../../../models/PripremaPrometaModel'
import {ToastContainer} from 'react-toastify'
import {prometTrakePaneli} from '../../../api/thermoPrometRolne'
import notifyToast from '../../../components/Toast/Toast'
import {TableActionButton} from '../../../components/TableActionBtn/TableActionButton'
import LokacijeRsTraka from "../../../components/LokacijeRSTraka/LokacijeRsTraka";


const PaneliPrometTraka = () => {
    const {id} = useParams<{id: string}>()
    const navigate = useNavigate()

    const [data, setData] = useState<PripremaPrometaModel[]>([])
    const [total, setTotal] = useState<number>(0)
    const [tezinaRolne, setTezinaRolne] = useState<number>(0)
    const [brojRolne, setBrojRolne] = useState<number>(0)
    const [krajnjaKolicinaRolne, setKrajnjaKolicinaRolne] = useState<string>('')

    // ✅ new: Lokacija trake
    const [lokacijaTrake, setLokacijaTrake] = useState<string>('')

    // Optional: derived suggestion (safer to memo)
    const prijedlogKrajnje = useMemo(() => Math.round(tezinaRolne - total), [tezinaRolne, total])

    useEffect(() => {
        const fetchData = async () => {
            try {
                const params = {trakaId: id}
                const result = await getNaloziZaTraku(params)

                setData(result.data)

                // guard if empty
                if (result.data?.length) {
                    setTezinaRolne(result.data[0].traka.Tezina)
                    setBrojRolne(result.data[0].repromaterijal.ReprMatId)
                    calculateTotal(result.data)
                }
            } catch (error) {
                console.error('Error fetching data:', error)
                notifyToast(false, 'Greška pri učitavanju podataka.')
            }
        }

        fetchData()
    }, [id])

    const calculateTotal = (rows: any[]) => {
        const t = rows.reduce((acc, item) => acc + parseFloat(item.KolicinaRolnePotrebna), 0)
        setTotal(t)
    }

    const handleKolicinaChange = (index: number, value: string) => {
        const newData = [...data]
        newData[index].KolicinaRolnePotrebna = value
        setData(newData)
        calculateTotal(newData)
    }

    const adjustQuantities = (rows: any[], krajnjaKolicina: number, tezina: number) => {
        const currentTotal = rows.reduce((acc, item) => acc + parseFloat(item.KolicinaRolnePotrebna), 0)
        const difference2 = tezina - krajnjaKolicina
        const difference = difference2 - currentTotal
        const adjustment = difference / rows.length

        return rows.map((item) => ({
            ...item,
            KolicinaRolnePotrebna: (parseFloat(item.KolicinaRolnePotrebna) + adjustment).toFixed(2),
        }))
    }

    const handleKrajnjaKolicinaChange = (e: any) => {
        const value = e.target.value
        setKrajnjaKolicinaRolne(value)

        if (value && data.length) {
            const adjustedData = adjustQuantities(data, parseFloat(value), tezinaRolne)
            setData(adjustedData)
            calculateTotal(adjustedData)
        }
    }

    const handleButtonClick = async () => {
        const additionalData = {
            brojTrake: id,
            krajnjaKolicina: krajnjaKolicinaRolne,
            // ✅ include lokacija
            lokacijaTrake: lokacijaTrake,
        }

        const payload = data.map((item) => ({
            brojNaloga: item.BrojNaloga,
            stavka: item.Stavka,
            gornjiLim: item.RolnaG,
            KolicinaRolnePotrebna: item.KolicinaRolnePotrebna,
            skeniranaTraka: item.Skenirano,
            ...additionalData,
        }))

        try {
            const response: any = await prometTrakePaneli(payload)
            const responseData = response

            if (responseData.error === 0) {
                notifyToast(true, responseData.message)

                setTimeout(() => {
                    navigate('/terminali/termopaneli/promet-rolne', {
                        replace: true,
                        state: {refresh: true},
                    })
                }, 3000)
            } else {
                notifyToast(false, responseData.message || 'An unexpected error occurred.')
            }
        } catch (error) {
            notifyToast(false, 'An unexpected error occurred.')
        }
    }

    const onDelete = (rowId: any) => {
        obrisiStavkuSaPripremePaneli(rowId)
            .then((response) => {
                notifyToast(true, response.data['message'])
                navigate('termopaneli/promet-rolne', {state: {refresh: true}})
            })
            .catch((error) => {
                notifyToast(false, error?.data?.['message'] || 'Greška.')
            })
    }

    const onEdit = (rowId: any) => {
        notifyToast(false, 'Izmjena je van upotrebe u modulu!')
    }

    return (
        <KTCard>
            <ToastContainer />

            <KTCardBody className='py-4'>
                <div className='container-fluid px-0 px-md-3'>
                    {/* Header */}
                    <div className='d-flex flex-column flex-md-row align-items-start align-items-md-center justify-content-between gap-3 mb-4'>
                        <div>
                            <h1 className='fw-bolder mb-1'>
                                Promet trake: <span className='text-success'>{id}</span>
                            </h1>
                            <div className='text-muted'>
                                Podesi krajnju težinu i raspodijeli količine po nalozima.
                            </div>
                        </div>

                        <div className='d-flex gap-2'>
                            <Button
                                variant='outline-secondary'
                                className='px-4'
                                onClick={() => navigate(-1)}
                            >
                                Nazad
                            </Button>
                        </div>
                    </div>

                    {/* Summary cards */}
                    <div className='row g-3 mb-4'>
                        <div className='col-12 col-lg-4'>
                            <div className='card shadow-sm border-0 h-100'>
                                <div className='card-body'>
                                    <div className='text-muted small'>Rolna</div>
                                    <div className='fs-3 fw-bolder text-gray-900'>
                                        R-{brojRolne || '-'}
                                    </div>

                                    <div className='mt-3 text-muted fs-2'>Trenutna težina trake</div>
                                    <div className='fw-semibold'>
                                        <span className='text-success fs-2'>{tezinaRolne || 0}</span> kg
                                    </div>
                                </div>
                            </div>
                        </div>

                        <div className='col-12 col-lg-4'>
                            <div className='card shadow-sm border-0 h-100'>
                                <div className='card-body'>
                                    <div className='text-muted fs-2'>Potrebna kolicina materijala</div>
                                    <div className='fs-3 fw-bolder text-gray-900 fs-2'>
                                        {Number.isFinite(total) ? total.toFixed(2) : '0.00'} kg
                                    </div>

                                    <div className='mt-3 text-muted small'>Prijedlog krajnje težine rolne</div>
                                    <div className='fw-semibold'>
                                        <span className='text-danger'>{Number.isFinite(prijedlogKrajnje) ? prijedlogKrajnje : 0}</span> kg
                                    </div>
                                </div>
                            </div>
                        </div>

                        <div className='col-12 col-lg-4'>
                            <div className='card shadow-sm border-0 h-100'>
                                <div className='card-body'>
                                    <div className='row g-3'>
                                        <div className='col-12'>
                                            <InputGroup>
                                                <InputGroup.Text className='bg-light'>
                                                    Krajnja težina trake
                                                </InputGroup.Text>
                                                <Form.Control
                                                    type='number'
                                                    value={krajnjaKolicinaRolne}
                                                    onChange={handleKrajnjaKolicinaChange}
                                                    placeholder='npr. 120'
                                                />
                                            </InputGroup>
                                            <div className='text-muted small mt-2'>
                                                Unos automatski koriguje količine po nalozima.
                                            </div>
                                        </div>

                                        {/* ✅ Lokacija trake */}
                                        <div className='col-12'>
                                            <div className='text-muted small mb-1'>Lokacija trake</div>
                                            <LokacijeRsTraka
                                                value={lokacijaTrake}
                                                onChange={(name: any, value: any) => setLokacijaTrake(value)}
                                                errors={undefined}
                                                touched={undefined}
                                            />
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Table */}
                    <div className='card shadow-sm border-0'>
                        <div className='card-body'>
                            <div className='d-flex flex-column flex-md-row align-items-start align-items-md-center justify-content-between gap-2 mb-3'>
                                <h3 className='fw-bolder mb-0'>Traka korištena za izradu naloga</h3>
                                <div className='text-muted small'>Ukupno stavki: {data.length}</div>
                            </div>

                            <div className='table-responsive'>
                                <Table hover className='align-middle mb-0'>
                                    <thead>
                                    <tr className='bg-primary text-white'>
                                        <th className='text-nowrap'>Broj naloga</th>
                                        <th className='text-nowrap'>Stavka</th>
                                        <th className='text-nowrap text-center'>Gornji</th>
                                        <th className='text-nowrap text-center'>Donji</th>
                                        <th className='text-nowrap'>Količina</th>
                                        <th className='text-nowrap text-center'>Skenirano</th>
                                        <th className='text-nowrap text-end'>Akcija</th>
                                    </tr>
                                    </thead>

                                    <tbody>
                                    {data.map((item, index) => (
                                        <tr key={item.Id}>
                                            <td className='fw-semibold text-nowrap'>{item.BrojNaloga}</td>
                                            <td style={{minWidth: 180}}>{item.Stavka}</td>

                                            <td className='text-center'>
                                                {item.RolnaG === 1 ? (
                                                    <i className='bi bi-check-circle-fill fs-4 text-success' />
                                                ) : (
                                                    <i className='bi bi-x-circle-fill fs-4 text-danger' />
                                                )}
                                            </td>

                                            <td className='text-center'>
                                                {item.RolnaG === 0 ? (
                                                    <i className='bi bi-check-circle-fill fs-4 text-success' />
                                                ) : (
                                                    <i className='bi bi-x-circle-fill fs-4 text-danger' />
                                                )}
                                            </td>

                                            <td style={{minWidth: 140}}>
                                                <Form.Control
                                                    type='number'
                                                    value={item.KolicinaRolnePotrebna}
                                                    onChange={(e) => handleKolicinaChange(index, e.target.value)}
                                                />
                                            </td>

                                            <td className='text-center'>
                                                {Number(item.Skenirano) === 1 ? (
                                                    <span className='badge bg-success'>DA</span>
                                                ) : (
                                                    <span className='badge bg-danger'>NE</span>
                                                )}
                                            </td>

                                            <td className='text-end'>
                                                <TableActionButton
                                                    onDelete={() => onDelete(item.Id)}
                                                    onEdit={() => onEdit(item.Id)}
                                                />
                                            </td>
                                        </tr>
                                    ))}

                                    {/* Total row */}
                                    <tr>
                                        <td colSpan={4} className='text-end'>
                                            <div className='fw-bolder'>Total:</div>
                                        </td>
                                        <td>
                                            <div className='fw-bolder'>{Number.isFinite(total) ? total.toFixed(2) : '0.00'}</div>
                                        </td>
                                        <td colSpan={2}></td>
                                    </tr>
                                    </tbody>
                                </Table>
                            </div>

                            <div className='text-muted small mt-3'>
                                Tip: Na mobitelu prevuci tabelu lijevo/desno.
                            </div>
                        </div>
                    </div>

                    {/* Action */}
                    <div className='card shadow-sm border-0 mt-4'>
                        <div className='card-body d-flex flex-column flex-md-row justify-content-between align-items-start align-items-md-center gap-3'>
                            <div className='text-muted'>
                                Nakon potvrde, promet trake će biti kreiran za odabrane naloge.
                            </div>

                            <div className='ms-md-auto'>
                                <Button
                                    onClick={handleButtonClick}
                                    className='btn btn-success btn-lg px-4'
                                    disabled={!krajnjaKolicinaRolne || !lokacijaTrake}
                                >
                                    <i className='bi bi-check2-circle me-2' />
                                    Kreiraj promet trake
                                </Button>
                                {(!krajnjaKolicinaRolne || !lokacijaTrake) && (
                                    <div className='text-muted small mt-2'>
                                        Unesi krajnju težinu i lokaciju prije potvrde.
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                </div>
            </KTCardBody>
        </KTCard>
    )
}

export default PaneliPrometTraka
